import type { LazyExoticComponent, ReactElement } from 'react';
import React, { Suspense } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { color, system } from 'styled-system';

import sx from '../../internal/styles/sx';
import { shouldForwardProp } from '../../internal/styles/utils';
import type { Glyphs } from './generated.types';
import { GlyphMap } from './GlyphMap';
import type { GlyphProps, SvgContentProps, SvgProps } from './types';

const glyphSize = {
  TINY: 'tiny',
  SMALL: 'small',
  BASE: 'base',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge',
} as const;

export const Skeleton = () => (
  <rect
    className="not-overwrite-fill"
    width="24"
    height="24"
    fill="transparent"
  />
);

export const SvgContent = <
  Category extends keyof Glyphs,
  Name extends Glyphs[Category],
>({
  category,
  name,
}: SvgContentProps<Category, Name>) => {
  const Component: LazyExoticComponent<() => ReactElement> =
    GlyphMap[category]?.[name];
  if (!Component) {
    return <Skeleton />;
  }
  return <Component />;
};

export const Svg = styled('svg', {
  shouldForwardProp,
})<SvgProps>(
  color,
  system({
    verticalAlign: true,
    size: {
      properties: ['width', 'height'],
      scale: 'iconSizes',
    },
  }),
  {
    fill: 'currentColor',
    ['[fill]:not(.not-overwrite-fill)']: {
      fill: 'currentColor',
    },
  },
  sx
);

export const Glyph = <
  Category extends keyof Glyphs,
  Name extends Glyphs[Category],
>({
  category,
  name,
  size = glyphSize.MEDIUM,
  ...props
}: GlyphProps<Category, Name>) => (
  // @ts-expect-error TODO: Fix viewBox type in next ticket OF2-3365
  <Svg viewBox="0 0 24 24" size={size} {...props}>
    <Suspense fallback={<Skeleton />}>
      <SvgContent category={category} name={name} />
    </Suspense>
  </Svg>
);

Glyph.propTypes = {
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
  // TODO: Research how to prevent PropTypes from interfering with TypeScript, because right now it causes the types of the name property to fail.
  // name: PropTypes.string.isRequired,
  // category: PropTypes.string.isRequired as PropTypes.Validator<keyof Glyphs>,
};

export default Glyph;
export { glyphSize };
